
import numeral from 'numeral';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  onMounted,
  computed,
} from 'vue';

import {
  GenericObject,
  Projects,
  Statistics,
} from '@/types';

import {
  PRESELECTED_PROJECTS_IDS,
  ENTRIES,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

export default defineComponent({
  components: {
    Chartist: defineAsyncComponent(() => import('@/components/Chartist.vue')),
  },
  props: {
    subtitle: {
      type: String,
      required: true,
    },
    showPublisherDropdown: {
      type: Boolean,
      default: false,
    },
    showAdvertiserDropdown: {
      type: Boolean,
      default: false,
    },
    showFeedDropdown: {
      type: Boolean,
      default: false,
    },
    hideDropdowns: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entriesLoading: statisticsLoading,
      entriesFetched: statisticsFetched,
      entries: statistics,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>(props.apiUrl, ENTRIES);

    const statisticsFilters = reactive<GenericObject>({
      project_id: user.isSuperAdmin() ? PRESELECTED_PROJECTS_IDS : [],
      publisher_id: [],
      advertiser_id: [],
      feed_id: [],
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const feedsParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const chartistKeyCounter = ref<number>(0);

    const fetchStatisticsWrapper = async (): Promise<void> => {
      await fetchStatistics(statisticsFilters);

      chartistKeyCounter.value += 1;
    };

    const onProjectChange = async (): Promise<void> => {
      statisticsFilters.publisher_id = [];

      statisticsFilters.advertiser_id = [];

      statisticsFilters.feed_id = [];

      await fetchStatisticsWrapper();
    };

    const onFilterDropdownChange = async (): Promise<void> => fetchStatisticsWrapper();

    const axisYLabelInterpolationFunction = (value: number): string => numeral(value).format('$0.0[0000]');

    onMounted(() => Promise.all([
      user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
      fetchStatisticsWrapper(),
    ]));

    return {
      user,
      projectsLoading,
      projects,
      statisticsLoading,
      statisticsFetched,
      statistics,
      statisticsFilters,
      usersParams,
      feedsParams,
      chartistKeyCounter,
      onProjectChange,
      onFilterDropdownChange,
      axisYLabelInterpolationFunction,
    };
  },
});
